import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Card, Alert, Badge } from 'react-bootstrap';
import { getSettlementGroups, createSettlementGroup, getDrivers, getVehicles } from '../api';

const SettlementGroupManager = () => {
    const [groups, setGroups] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });

    const loadGroups = useCallback(async () => {
        setLoading(true);
        try {
            const [gData, dData, vData] = await Promise.all([
                getSettlementGroups(),
                getDrivers({ limit: 1000 }),
                getVehicles({ limit: 1000 })
            ]);
            setGroups(gData);
            setDrivers(dData);
            setVehicles(vData);
            setError(null);
        } catch (err) {
            setError("Error al cargar los datos de los grupos de liquidación.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadGroups();
    }, [loadGroups]);

    const handleOpenCreate = () => {
        setFormData({ name: '', description: '' });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createSettlementGroup(formData);
            setShowModal(false);
            loadGroups();
        } catch (err) {
            alert("Error al crear el grupo. Asegúrese de que el nombre sea único.");
        }
    };

    return (
        <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>📋 Grupos de Liquidación</h5>
                <Button variant="primary" size="sm" onClick={handleOpenCreate}>
                    + Nuevo Grupo
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="shadow-sm border-0">
                <Table hover responsive className="align-middle mb-0">
                    <thead className="bg-light">
                        <tr>
                            <th>ID</th>
                            <th>Nombre del Grupo</th>
                            <th>Móviles y Conductores</th>
                            <th>Descripción / Notas</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map(g => (
                            <tr key={g.id}>
                                <td>{g.id}</td>
                                <td className="fw-bold">{g.name}</td>
                                <td>
                                    <div>
                                        <strong className="text-secondary small">Móviles:</strong>{' '}
                                        {vehicles.filter(v => v.settlement_group_id === g.id).map(v => (
                                            <Badge key={v.id} bg="secondary" className="me-1">
                                                {v.internal_code} ({v.plate})
                                            </Badge>
                                        ))}
                                        {vehicles.filter(v => v.settlement_group_id === g.id).length === 0 && <span className="text-muted small">Ninguno</span>}
                                    </div>
                                    <div className="mt-1">
                                        <strong className="text-secondary small">Conductores:</strong>{' '}
                                        {drivers.filter(d => d.settlement_group_id === g.id).map(d => (
                                            <Badge key={d.id} bg="primary" className="me-1">
                                                {d.full_name}
                                            </Badge>
                                        ))}
                                        {drivers.filter(d => d.settlement_group_id === g.id).length === 0 && <span className="text-muted small">Ninguno</span>}
                                    </div>
                                </td>
                                <td className="text-muted small">{g.description || '-'}</td>
                                <td>
                                    <Badge bg="info">Activo</Badge>
                                </td>
                            </tr>
                        ))}
                        {groups.length === 0 && !loading && (
                            <tr>
                                <td colSpan="4" className="text-center p-4 text-muted">
                                    No hay grupos creados. Crea uno para agrupar tus liquidaciones.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </Card>

            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Form onSubmit={handleSubmit}>
                    <Modal.Header closeButton>
                        <Modal.Title>Crear Grupo de Liquidación</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Nombre del Grupo</Form.Label>
                            <Form.Control 
                                required 
                                placeholder="Ej: Semanal, Quincenal, Socios VIP..." 
                                value={formData.name}
                                onChange={e => setFormData({...formData, name: e.target.value})}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Descripción (Opcional)</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={2}
                                placeholder="Notas adicionales sobre este grupo..." 
                                value={formData.description}
                                onChange={e => setFormData({...formData, description: e.target.value})}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="primary" type="submit">Crear Grupo</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default SettlementGroupManager;
